'use client';

import { Box } from '@ironhack/design-system2/components/box';
import { Button } from '@ironhack/design-system2/components/button';
import { Checkbox } from '@ironhack/design-system2/components/checkbox';
import { Flex } from '@ironhack/design-system2/components/flex';
import { Text } from '@ironhack/design-system2/components/text';
import { Tooltip } from '@ironhack/design-system2/components/tooltip';
import { getCookie } from 'cookies-next';
import {
  type ChangeEvent,
  type ReactElement,
  useEffect,
  useState,
} from 'react';
import { useToggle } from 'react-use';

import { StructuredContent } from '@/app/components/client/structured-content';
import { getCategoryCookieAmount } from '@/lib/cookiebot';
import { type Cookiebot } from '@/lib/cookiebot/types';
import { type CookieBannerText, type CookieCategoryKey } from './types';

type CookiePreferences = Record<
  Exclude<CookieCategoryKey, 'necessary'>,
  boolean
>;

type Props = {
  cookiebot: Cookiebot | null;
  text: CookieBannerText | null;
};

export const CookieBanner = (props: Props): ReactElement | null => {
  const { cookiebot, text } = props;

  const cookieConsent = getCookie('CookieConsent');

  // Only show the banner if the user's preferences aren't saved in cookies yet
  const [showBanner, setShowBanner] = useState<boolean>(!cookieConsent);
  const [showSettings, toggleShowSettings] = useToggle(true);
  const [cookiePreferences, setCookiePreferences] = useState<CookiePreferences>(
    {
      preferences: false,
      statistics: false,
      marketing: false,
    }
  );
  useEffect(() => {
    cookiebot?.hide();
  }, [cookiebot]);

  if (!showBanner || !text) {
    return null;
  }

  const decline = () => {
    cookiebot?.withdraw();
    setShowBanner(false);
  };

  const acceptSelected = () => {
    cookiebot?.submitCustomConsent(
      cookiePreferences.preferences,
      cookiePreferences.statistics,
      cookiePreferences.marketing
    );
    setShowBanner(false);
  };

  const acceptAll = () => {
    cookiebot?.submitCustomConsent(true, true, true);
    setShowBanner(false);
  };

  return (
    <Box
      bg="#d3d3d380"
      h="100vh"
      id="cookie-banner-backdrop"
      position="fixed"
      top="0"
      w="100vw"
      zIndex={1300}
    >
      <Flex
        bgColor="background.white"
        borderRadius="lg"
        boxShadow="0px 8px 16px 0px rgba(0, 0, 0, 0.16)"
        flexDirection="column"
        id="cookie-banner-modal"
        left="50%"
        p={2}
        position="fixed"
        top="50%"
        transform="translateX(-50%) translateY(-50%)"
        transition="bottom 250ms ease-in-out"
        w="328px"
      >
        <Flex alignItems="center" as="span" gap={1} mb={1.5}>
          <Text as="span" display="flex" gap={0.5} textStyle="2xl">
            <Text as="span">{'🍪'}</Text>
            <Text as="span">{'😋'}</Text>
          </Text>
          <Text
            as="span"
            color="text.primary"
            textStyle="lBold"
            whiteSpace="nowrap"
          >
            {text.title}
          </Text>
        </Flex>
        {text.description && (
          <StructuredContent data={text.description} mb={2} textStyle="m" />
        )}

        <Flex
          borderBottom={showSettings ? '1px solid #0003' : '0px solid #0000'}
          borderTop={showSettings ? '1px solid #0003' : '0px solid #0000'}
          justifyContent="space-around"
          mb={showSettings ? 2 : 0}
          py={showSettings ? 2 : 0}
          transition={
            showSettings
              ? 'border-color 200ms ease-in-out 100ms, border-width 200ms ease-in-out 100ms, margin 200ms ease-in-out, padding 200ms ease-in-out 100ms'
              : 'border-color 100ms ease-in-out, border-width 100ms ease-in-out, margin 100ms ease-in-out 100ms, padding 100ms ease-in-out'
          }
        >
          {showSettings && (
            <Flex wrap="wrap">
              {text.categories.map(({ description, key, name }) => {
                const cookieAmount = getCategoryCookieAmount(
                  key,
                  cookiebot?.dialog
                );
                return (
                  <Checkbox
                    defaultChecked={
                      key === 'necessary' ? true : cookiePreferences[key]
                    }
                    id={`cookie-banner__${key}`}
                    justifyContent="start"
                    key={key}
                    mx={[null, null, 'auto']}
                    my={0.5}
                    textStyle="s"
                    w="50%"
                    {...(key === 'necessary'
                      ? { disabled: true }
                      : {
                          onChange: (e: ChangeEvent<HTMLInputElement>) =>
                            setCookiePreferences({
                              ...cookiePreferences,
                              [key]: e.target.checked,
                            }),
                        })}
                  >
                    <Tooltip hasArrow label={description}>
                      <Text textStyle="m">
                        {name.replace('{amount}', cookieAmount)}
                      </Text>
                    </Tooltip>
                  </Checkbox>
                );
              })}
            </Flex>
          )}
        </Flex>

        <Button
          alignSelf="center"
          mb={2}
          onClick={toggleShowSettings}
          px={2.5}
          py={1}
          size="s"
          textDecoration="underline"
          textStyle="m"
          variant="tertiary"
          width="min-content"
        >
          {showSettings ? text.hideDetailsText : text.showDetailsText}
        </Button>

        <Flex gap={1} justifyContent="space-between">
          <Button
            onClick={decline}
            px={2}
            py={1}
            size="s"
            textStyle="m"
            variant="secondary"
            w="50%"
          >
            {text.declineButtonText}
          </Button>

          <Button
            onClick={showSettings ? acceptSelected : acceptAll}
            size="s"
            textStyle="m"
            variant="primary"
            w="50%"
          >
            {showSettings
              ? text.acceptSelectedButtonText
              : text.acceptAllButtonText}
          </Button>
        </Flex>
      </Flex>
    </Box>
  );
};
